import React, { useState } from 'react';
import 'highlight.js/styles/github.css'; // Import a syntax highlighting style
import hljs from 'highlight.js/lib/core';
import sql from 'highlight.js/lib/languages/sql';
import axios from 'axios';
import { getToken } from '../../utils/TokenUtils';
import { API_URL, ENDPOINTS } from '../../constants/api';
import { queryResultsTable } from '../../utils/ktcUtils';

// Register SQL language for syntax highlighting
hljs.registerLanguage('sql', sql);

const SQLQueryPage = () => {
  const [query, setQuery] = useState(''); // Query input state
  const [result, setResult] = useState(null); // Query result state
  const [rowsAffected, setRowsAffected] = useState(null); // Rows affected for non-select queries
  const [error, setError] = useState(null); // Error state
  const [isNestedArray, setIsNestedArray] = useState(false);
  const token = getToken();

  // Function to execute query
  const executeQuery = async () => {
    try {
      setError(null);
      setResult(null);
      setRowsAffected(null);

      // Mock API call to execute query (replace with your API endpoint)
      const response = await axios.post(
        `${API_URL}/${ENDPOINTS.SYS}/execute-query`,
        { query },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );
      const { data } = response;

      //  console.log('data:', data);

      // Check if the response includes rows or just metadata
      if (Array.isArray(data) && data.length > 0) {
        const isNestedArray = data.some((data) => Array.isArray(data));
        setIsNestedArray(isNestedArray);
        setResult(data); // Handle SELECT query results
      } else if (data.affectedRows !== undefined) {
        setRowsAffected(data.affectedRows); // Handle INSERT, UPDATE, DELETE
      } else {
        setResult([]); // No data returned (e.g., empty SELECT result)
      }
      if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      // Handle errors
      setError(
        err.response?.data?.message || `Failed to execute query. ${err.message}`
      );
    }
  };

  //   console.log('result:', result);
  //   console.log('affected:', rowsAffected);

  return (
    <div className="container mt-5">
      <h2 className="mb-4">SQL Query Executor</h2>
      <div className="mb-3">
        <label htmlFor="query" className="form-label">
          Enter SQL Query:
        </label>
        <textarea
          id="query"
          className="form-control"
          rows="5"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Write your SQL query here..."
        ></textarea>
      </div>
      <div className="mb-3">
        <label htmlFor="preview" className="form-label">
          Query Preview (Syntax Highlighted):
        </label>
        <pre
          id="preview"
          className="p-3 bg-light rounded border"
          style={{ whiteSpace: 'pre-wrap', overflowX: 'auto' }}
          dangerouslySetInnerHTML={{
            __html: hljs.highlight(query, { language: 'sql' }).value,
          }}
        ></pre>
      </div>
      <button
        className="btn btn-primary"
        onClick={executeQuery}
        disabled={!query.trim()}
      >
        Execute Query
      </button>

      {result && Array.isArray(result) && result.length > 0 && (
        <div className="mt-4">
          <h2>Query Results</h2>
          {isNestedArray ? (
            result.map((result, index) => {
              if (Array.isArray(result)) {
                return (
                  <div key={index} className="mb-4">
                    <h4>Result Set {index + 1} (Array)</h4>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          {Object.keys(result[0] || {}).map((key) => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {Object.values(row).map((value, colIndex) => (
                              <td key={colIndex}>
                                {value !== null ? value : 'NULL'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              } else {
                // For non-array results
                return (
                  <div key={index} className="mb-4">
                    <h4>Result Set {index + 1}</h4>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          {Object.keys(result).map((key) => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {Object.values(result).map((value, colIndex) => (
                            <td key={colIndex}>
                              {value !== null ? value : 'NULL'}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              }
            })
          ) : (
            <div className="mb-4">
              <h4>Single Result</h4>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    {Object.keys(result[0] || {}).map((key) => (
                      <th key={key}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {Object.values(row).map((value, colIndex) => (
                        <td key={colIndex}>
                          {value !== null ? value : 'NULL'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {rowsAffected !== null && (
        <div className="mt-4">
          <h4>Query Executed Successfully</h4>
          <p>{rowsAffected} row(s) affected.</p>
        </div>
      )}

      {result &&
        (!Array.isArray(result) || result.length === 0) &&
        rowsAffected === null && (
          <div className="mt-4">
            <h4>No data returned from the query.</h4>
          </div>
        )}

      {error && (
        <div className="mt-4 text-danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      <br />
      <br />
    </div>
  );
};

export default SQLQueryPage;
